import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Button,
  ButtonBase,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { colors } from '../../styles';
import {
  FeeType,
  Site,
  SiteStatus,
  useValidateRenewalQuery,
  useGetTopupTimelineLazyQuery,
  SiteTimelineStep,
} from '../../generated/graphql';
import { ClipLoader } from 'react-spinners';
import { useParams, useHistory } from 'react-router-dom';
import Carousel from 'react-material-ui-carousel';
import 'react-credit-cards/es/styles-compiled.css';
import { Tooltip } from '../../shared/components/Tooltip';
import { useReadOTP } from 'react-read-otp';
import { CardToken } from '../../shared/types/card-token';
import { isSpecialDomain } from '../../shared/utils/specialTariff';
import { PaymentType } from '../../shared/types/paymentType';
import { PostPayment } from './PostPayment';
import { PlateSelector } from '../../shared/components/PlateSelector';
import { useVrm } from '../../shared/hooks/useVrm';
import { useDuration } from '../../shared/hooks/useDuration';
import { useDeleteObject } from '../../shared/hooks/useDeleteObject';
import { useReceiveCommunication } from '../../shared/hooks/useReceiveCommunication';
import { isChipSelected } from '../../shared/utils/chips';
import { useSiteSelector } from '../../shared/hooks/useSiteSelector';
import { useCards } from '../../shared/hooks/useCards';
import { getSpecialTariffText } from '../../shared/utils/specialTariffs';
import { plateEquals, resolvePlateSelection } from '../../shared/utils/plates';
import { isValidEmail } from '../../shared/utils/email';
import { useStepHandler } from '../../shared/hooks/useStepHandler';
import { useThemeSelector } from '../../shared/contexts/ThemeContext';
import { SiteNotificationsAlerts } from '../../shared/components/SiteNotificationsAlerts';
import { t } from 'i18next';
import { PaymentFlow } from '../../shared/components/PaymentFlow/PaymentFlow';
import Settings from '@mui/icons-material/Settings';
import { DateTime } from 'luxon';
import { ZONE } from '../../shared/constants';
import {
  getFixedTimeBoundary,
  getSessionStart,
  getTimelineSessionTiming,
  logPaymentDurationMismatch,
} from './helper';

interface IParams {
  qrCode?: string;
  sessionCode?: string;
}

interface UserPlate {
  id: number;
  vrm: string;
}

interface IProps {
  postPaymentSite?: Site;
  postPaymentVrm?: string;
}

export const Home: React.FC<IProps> = ({
  postPaymentSite,
  postPaymentVrm,
}: IProps) => {
  // When a post-payment site finds no active session, PostPayment re-renders
  // Home with these props to run the standard prepay timeline UI for that
  // POSTPAYMENT site. Treat that case as prepay so the server-priced timeline
  // drives both duration stepping and the displayed amount — otherwise the
  // amount stays $0 in the UI while Windcave still charges the real price.
  const isPostPaymentPrepayFallback = Boolean(
    postPaymentSite && postPaymentVrm,
  );
  const [showPaymentFlow, setShowPaymentFlow] = useState<boolean>(false);
  const history = useHistory();
  // Use ref for currentTime to avoid triggering re-renders, only update display state when needed
  const currentTimeRef = useRef<DateTime>(DateTime.now().setZone(ZONE));
  const [currentTime, setCurrentTime] = useState<DateTime>(
    currentTimeRef.current,
  );
  const { qrCode, sessionCode } = useParams<IParams>();
  const [checkedCreditCard, setCheckedCreditCard] = useState<boolean>(false);
  const [emailTouched, setEmailTouched] = useState<boolean>(false);
  const [name] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [tokens, setTokens] = useState<CardToken[]>([]);
  const [plates, setPlates] = useState<UserPlate[]>([]);
  const [selectedCard, setSelectedCard] = useState<string>('');
  const [selectedVrm, setSelectedVrm] = useState<string>('new');
  // Last plate the user typed by hand. Held separately so stepping to a stored
  // plate does not lose it; `selectedVrm === 'new'` means `vrm` is that plate.
  const [typedVrm, setTypedVrm] = useState<string>('');
  const [selectedFee, setSelectedFee] = useState<FeeType>(FeeType.Hourly);
  const bottomRef = useRef<null | HTMLButtonElement>(null);
  const [loadedBySpecialTariff, setLoadedBySpecialTariff] =
    useState<boolean>(false);
  const [specialTariffSelected, setSpecialTariffSelected] = useState<
    number | null
  >(null);
  const [fetchingTopupTimeline, setFetchingTopupTimeline] =
    useState<boolean>(false);
  const [topupTimelineLoaded, setTopupTimelineLoaded] =
    useState<boolean>(false);
  const { vrm, VrmComponent, setVrm } = useVrm();
  const {
    reachedMaxDuration,
    setReachedMaxDuration,
    changeSelectedFee,
    decreaseDuration,
    duration,
    setDuration,
    increaseDuration,
    setTimeSteps,
    timelineIndex,
    setTimelineIndex,
  } = useDuration(isPostPaymentPrepayFallback);
  const {
    payWithTokenLoading,
    paymentLinkLoading,
    validateMfaLoading,
    requestToken,
    mfa,
    setMfa,
    MFAComponent,
    countdown,
    TimerComponent,
    step,
    setStep,
    processPayment,
    hasTokenLoading,
  } = useStepHandler(
    setError,
    bottomRef,
    setTokens,
    setPlates,
    setSelectedCard,
    setSelectedVrm,
    setVrm,
  );
  const { DeletePopover, markCardToDelete, markPlateToDelete } =
    useDeleteObject(setError, setPlates, setTokens, setStep, setSelectedCard);
  const {
    email,
    setEmail,
    EmailComponent,
    phone,
    setPhone,
    PhoneComponent,
    receiveEmails,
    setReceiveEmails,
    receiveSms,
    setReceiveSms,
    ReceiveEmailComponent,
    ReceiveSMSComponent,
  } = useReceiveCommunication();
  const {
    location,
    selectedSite,
    sites,
    sitesLoading,
    SiteSelectorComponent,
    activeSiteNotifications,
    setSiteInfo,
  } = useSiteSelector(qrCode, setDuration, setTimeSteps);
  const [dismissedNotificationIds, setDismissedNotificationIds] = useState(
    () => new Set<number>(),
  );
  const dismissSiteNotification = useCallback((id: number) => {
    setDismissedNotificationIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);
  const globalSiteNotifications = useMemo(
    () => activeSiteNotifications.filter((n) => n.siteId == null),
    [activeSiteNotifications],
  );
  const selectedSiteNotifications = useMemo(() => {
    if (!selectedSite) {
      return [];
    }
    return activeSiteNotifications.filter(
      (n) => n.siteId != null && Number(n.siteId) === Number(selectedSite.id),
    );
  }, [activeSiteNotifications, selectedSite]);
  const { getCards } = useCards();
  const { selectedTheme } = useThemeSelector();
  const [isSessionStale, setIsSessionStale] = useState<boolean>(false);
  const wasTopupTimelineLoaded = useRef<boolean>(false);
  const siteOriginalTimeline = useRef<SiteTimelineStep[]>(
    selectedSite?.timeline ?? [],
  );

  const [getTopupTimeline, { data: topupTimelineData }] =
    useGetTopupTimelineLazyQuery();

  const hasTopupTimeline = useMemo(() => {
    return (
      !!topupTimelineData?.getTopupTimeline &&
      topupTimelineData?.getTopupTimeline.length > 0
    );
  }, [topupTimelineData?.getTopupTimeline]);

  const fetchAndApplyTopupTimeline = async () => {
    if (!selectedSite || !vrm || vrm.trim().length === 0) {
      return;
    }

    // Only fetch for PREPAYMENT sites
    if (selectedSite.paymentType !== PaymentType.PREPAYMENT) {
      return;
    }

    setFetchingTopupTimeline(true);

    try {
      const result = await getTopupTimeline({
        variables: {
          vrm: vrm.trim(),
          siteId: selectedSite.id,
        },
        fetchPolicy: 'network-only',
      });

      const { data } = result;

      if (data?.getTopupTimeline && data.getTopupTimeline.length > 0) {
        // User has active ticket - replace timeline with topup timeline
        if (!wasTopupTimelineLoaded.current) {
          siteOriginalTimeline.current = selectedSite?.timeline ?? [];
          wasTopupTimelineLoaded.current = true;
        }

        const updatedSite = {
          ...selectedSite,
          timeline: data.getTopupTimeline,
        };
        setSiteInfo(updatedSite, { preserveDuration: true });

        // Set flag to trigger timeline reset in useEffect
        setTopupTimelineLoaded(true);
      } else {
        // Only restore if we previously swapped in a topup timeline. Otherwise
        // siteOriginalTimeline.current is still its useRef default ([]), and
        // writing it back would wipe the site's real getSites timeline.
        if (wasTopupTimelineLoaded.current) {
          setSiteInfo(
            {
              ...selectedSite,
              timeline: siteOriginalTimeline.current,
            },
            { preserveDuration: true },
          );
          wasTopupTimelineLoaded.current = false;
        }
        setTopupTimelineLoaded(false);
      }
      // If empty array, continue with original timeline (no changes needed)
    } catch (error) {
      console.error('Error fetching topup timeline:', error);
      // Continue with original timeline on error
    } finally {
      setFetchingTopupTimeline(false);
    }
  };

  // Trigger topup-timeline lookup whenever VRM + site are both available.
  // Without this, deep-link / stored-VRM flows skip the lookup entirely and
  // the user ends up paying from-now instead of extending from untilTime.
  useEffect(() => {
    if (
      selectedSite &&
      selectedSite.paymentType === PaymentType.PREPAYMENT &&
      vrm &&
      vrm.trim().length > 0
    ) {
      fetchAndApplyTopupTimeline();
    }
    // Intentionally not depending on fetchAndApplyTopupTimeline (re-creates
    // each render); we only want to refire when the inputs actually change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSite?.id, vrm]);

  useReadOTP(setMfa, {
    enabled: step === 'token',
  });

  useEffect(() => {
    const STALE_TIMEOUT_MS = 10 * 60 * 1000;

    const timerId = window.setTimeout(() => {
      setIsSessionStale(true);
    }, STALE_TIMEOUT_MS);

    return () => {
      window.clearTimeout(timerId);
    };
  }, []);

  const handleStaleSessionRefresh = useCallback(() => {
    setIsSessionStale(false);
    window.location.reload();
  }, []);

  useEffect(() => {
    if (postPaymentSite) {
      setSiteInfo(postPaymentSite);
    }
    if (postPaymentVrm) {
      setVrm(postPaymentVrm);
    }
    setStep('initial');
  }, [postPaymentSite, postPaymentVrm]);

  const { data: session, loading: validateRenewalLoading } =
    useValidateRenewalQuery({
      variables: {
        sessionCode: sessionCode ?? '',
      },
    });

  // Track previous site ID to detect site changes
  const previousSiteIdRef = useRef<number | null>(null);

  // Reset VRM and step when site changes (excluding post-payment and renewal scenarios)
  useEffect(() => {
    if (
      selectedSite &&
      !postPaymentSite &&
      !postPaymentVrm &&
      previousSiteIdRef.current !== null &&
      previousSiteIdRef.current !== selectedSite.id &&
      !(
        session &&
        session.validateRenewal &&
        session.validateRenewal.successful
      )
    ) {
      // Site has changed, reset VRM and step
      setVrm('');
      setStep('newVrm');
    }
    // Update the ref with the current site ID
    if (selectedSite) {
      previousSiteIdRef.current = selectedSite.id;
    }
  }, [selectedSite, postPaymentSite, postPaymentVrm, session, setVrm]);

  // Set step to 'newVrm' when site is selected and VRM is empty (excluding post-payment and renewal scenarios)
  useEffect(() => {
    if (
      selectedSite &&
      !postPaymentSite &&
      !postPaymentVrm &&
      (!vrm || vrm.trim().length === 0) &&
      !['token', 'paytoken', 'newpayment'].includes(step) &&
      !(
        session &&
        session.validateRenewal &&
        session.validateRenewal.successful
      )
    ) {
      setStep('newVrm');
    }
  }, [selectedSite, vrm, postPaymentSite, postPaymentVrm, step, session]);

  useEffect(() => {
    if (
      session &&
      session.validateRenewal &&
      session.validateRenewal.successful
    ) {
      const { renewalInfo } = session.validateRenewal;
      if (renewalInfo) {
        const { siteId, duration, vrm, email, phone, tokens, feeType } =
          renewalInfo;
        const site = sites?.getSites.find((x) => x.id === siteId);
        console.log('Site', site);
        if (site) {
          setSiteInfo(site);
        }
        setSelectedFee(
          feeType === 'MONTHLY'
            ? FeeType.Monthly
            : feeType === 'WEEKLY'
              ? FeeType.Weekly
              : FeeType.Hourly,
        );
        setDuration(duration);
        setVrm(vrm);
        if (email) {
          setEmail(email);
          setReceiveEmails(true);
        }
        if (phone) {
          setPhone(phone);
          setReceiveSms(true);
        }
        if (tokens.length > 0) {
          setTokens(tokens);
          setSelectedCard(tokens[0].id);
          setStep('paytoken');
        } else {
          setStep('newpayment');
        }
      }
    }
  }, [session, sites]);

  useEffect(() => {
    if (!selectedSite) {
      const site = sites?.getSites.find((x) => isSpecialDomain(x));
      if (site) {
        setLoadedBySpecialTariff(true);
        setSiteInfo(site);
      }
      if (location) {
        const site = sites?.getSites.find((x) => x.qrCode === location);
        if (site) {
          setSiteInfo(site);
        }
      }
    }
  }, [sites]);

  const handleCreditCardChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setCheckedCreditCard(event.target.checked);
    if (event.target.checked) {
      setReceiveSms(true);
    }
  };

  const isPrepay =
    selectedSite?.paymentType === PaymentType.PREPAYMENT ||
    isPostPaymentPrepayFallback;
  const hasTiers = !!selectedSite?.pricingTiers?.length;
  const hasTimeline = !!selectedSite?.timeline?.length;
  // NOTE: previously `useTimeline` required `!hasTiers` — that was a legacy
  // guard from when buildTimeline used a flat costPerHour and would have
  // produced incorrect prices for tiered sites. buildTimeline now consults
  // PricingTier (see SitesTimelineService.buildTimeline + shared/utils/
  // pricing-tiers.ts), so tiered sites are safe to drive from the timeline.
  const useTimeline =
    Boolean(isPrepay && hasTimeline) && selectedFee === FeeType.Hourly;


  console.log(
    'timeline',
    selectedSite?.timeline,
    'isPrepay',
    isPrepay,
    'hasTiers',
    hasTiers,
    'hasTimeline',
    hasTimeline,
    'bool',
    Boolean(isPrepay && !hasTiers && hasTimeline),
    'selectedFee',
    selectedFee,
    'useTimeline',
    useTimeline,
    'timelineIndex',
    timelineIndex,
    'len',
    selectedSite?.timeline?.length,
  );

  // Hide buttons when loaded by special domain and timeline has only 1 step
  const shouldHideButtons = useMemo(() => {
    return (
      selectedSite &&
      isSpecialDomain(selectedSite) &&
      selectedSite.timeline?.length === 1
    );
  }, [selectedSite]);

  const currentTimelineStep = useMemo(() => {
    if (!useTimeline || !selectedSite?.timeline?.length) {
      return undefined;
    }
    if (timelineIndex < 0 || timelineIndex >= selectedSite.timeline.length) {
      return undefined;
    }
    return selectedSite.timeline[timelineIndex];
  }, [useTimeline, selectedSite, timelineIndex]);

  useEffect(() => {
    if (selectedSite) {
      changeSelectedFee(
        selectedFee,
        selectedSite,
        selectedFee === FeeType.SpecialTariff ? specialTariffSelected : null,
      );
    }
  }, [selectedFee, selectedSite, specialTariffSelected]);

  // Ensure timeline is reset to index 0 after topup timeline is loaded
  useEffect(() => {
    if (
      topupTimelineLoaded &&
      selectedSite?.timeline &&
      selectedSite.timeline.length > 0
    ) {
      setTimelineIndex(0);
      setDuration(selectedSite.timeline[0].durationMinutes);
      setReachedMaxDuration(selectedSite.timeline.length === 1);
      setTopupTimelineLoaded(false); // Reset flag
    }
  }, [topupTimelineLoaded, selectedSite?.timeline]);

  // Amount is sourced entirely from server data: timeline for hourly,
  // flat fields for weekly/monthly, specialTariffPricing for explicit picks.
  const amount = useMemo(() => {
    const fee = selectedSite?.transactionFee ?? 0;
    if (useTimeline && currentTimelineStep) {
      return currentTimelineStep.accumulatedCost;
    }
    if (!selectedSite) return 0;
    if (selectedFee === FeeType.Weekly) {
      return (selectedSite.weeklyFee ?? 0) + fee;
    }
    if (selectedFee === FeeType.Monthly) {
      return (selectedSite.monthlyFee ?? 0) + fee;
    }
    if (selectedFee === FeeType.SpecialTariff && specialTariffSelected != null) {
      const tariff = selectedSite.specialTariffPricing?.find(
        (t) => t.id === specialTariffSelected,
      );
      return (tariff?.price ?? 0) + fee;
    }
    return 0;
  }, [
    useTimeline,
    currentTimelineStep,
    selectedSite,
    selectedFee,
    specialTariffSelected,
  ]);

  const paidUntilTime = useMemo(() => {
    const now = currentTimeRef.current;

    if (useTimeline && currentTimelineStep) {
      if (currentTimelineStep.durationMinutes === undefined) {
        return null;
      }
      return getTimelineSessionTiming({
        now,
        timelineStep: currentTimelineStep,
        hasTopupTimeline,
        topupAnchorFixedTime: selectedSite?.timeline?.[0]?.fixedTime,
      }).paidUntilTime;
    }

    if (
      selectedFee === FeeType.SpecialTariff &&
      specialTariffSelected != null &&
      selectedSite
    ) {
      const tariff = selectedSite.specialTariffPricing?.find(
        (t) => t.id === specialTariffSelected,
      );
      if (tariff?.untilTime) {
        const [h, m] = tariff.untilTime.split(':');
        let limitTime = now.set({
          hour: parseInt(h),
          minute: parseInt(m),
          second: 0,
        });
        if (
          (tariff.end != null &&
            tariff.start != null &&
            tariff.end < tariff.start) ||
          (tariff.start != null && tariff.untilTime < tariff.start)
        ) {
          limitTime = limitTime.plus({ days: 1 });
        }
        return limitTime;
      }
      if (tariff?.sessionTime) {
        const unit = (tariff.sessionTimeUnit ?? 'hours').toLowerCase();
        return now.plus({ [unit]: tariff.sessionTime });
      }
    }

    if (duration <= 0) return null;
    // Weekly/Monthly use the duration set by useDuration (7d / 28d in minutes).
    return now.plus({ minutes: duration });
  }, [
    duration,
    useTimeline,
    currentTimelineStep,
    selectedFee,
    specialTariffSelected,
    selectedSite,
    currentTime,
    hasTopupTimeline,
  ]);

  const actualDuration = useMemo(() => {
    const now = currentTimeRef.current;

    if (useTimeline && currentTimelineStep) {
      return getTimelineSessionTiming({
        now,
        timelineStep: currentTimelineStep,
        hasTopupTimeline,
        topupAnchorFixedTime: selectedSite?.timeline?.[0]?.fixedTime,
      }).totalMinutes;
    }
    return Math.round(duration);
  }, [
    useTimeline,
    currentTimelineStep,
    duration,
    currentTime,
    hasTopupTimeline,
    selectedSite?.timeline,
  ]);

  const topupFixedTime: number | undefined = useMemo(() => {
    if (!selectedSite?.timeline?.[0]?.fixedTime || !hasTopupTimeline) {
      return undefined;
    }
    const fixedTimeBoundary = getFixedTimeBoundary(
      selectedSite.timeline[0].fixedTime,
      currentTimeRef.current,
    );
    return fixedTimeBoundary.toSeconds();
  }, [selectedSite?.timeline, currentTime, hasTopupTimeline]);

  const buildPaymentSessionDetails = useCallback(() => {
    const now = currentTimeRef.current;
    const timelineStepDurationMinutes =
      useTimeline && currentTimelineStep
        ? currentTimelineStep.durationMinutes
        : undefined;

    logPaymentDurationMismatch({
      totalMinutes: actualDuration,
      paidUntilTime,
      now,
      // A topup's window starts at the active ticket's untilTime, so the
      // invariant is paid-until minus that anchor — not minus now.
      startTime: getSessionStart({
        now,
        hasTopupTimeline,
        topupAnchorFixedTime: selectedSite?.timeline?.[0]?.fixedTime,
      }),
      timelineIndex,
      currentTimelineStep,
      durationState: duration,
      timelineStepDurationMinutes,
    });

    return {
      vrm,
      name,
      email,
      phone,
      // Authoritative total session minutes (now → paid-until). The API must
      // use this for ticket untilTime; timelineStepDurationMinutes is step-only
      // for pricing (accumulatedCost / special-tariff billing).
      duration: actualDuration,
      receiveEmails,
      receiveSms,
      reachedMaxDuration,
      specialTariffSelected,
      selectedFee,
      checkedCreditCard,
      selectedCard,
      timelineStepDurationMinutes,
      timelineStepAccumulatedCost:
        useTimeline && currentTimelineStep
          ? currentTimelineStep.accumulatedCost
          : undefined,
      timelineStepSpecialTariffId:
        useTimeline && currentTimelineStep
          ? currentTimelineStep.specialTariffId
          : undefined,
      topupFixedTime,
      parkingTicketSessionId: selectedSite?.parkingTicketSessionId ?? undefined,
    };
  }, [
    actualDuration,
    paidUntilTime,
    hasTopupTimeline,
    timelineIndex,
    currentTimelineStep,
    duration,
    useTimeline,
    vrm,
    name,
    email,
    phone,
    receiveEmails,
    receiveSms,
    reachedMaxDuration,
    specialTariffSelected,
    selectedFee,
    checkedCreditCard,
    selectedCard,
    topupFixedTime,
    selectedSite,
  ]);

  const durationCondition = actualDuration > 0;

  const choosePaymentMethod = (method: string) => {
    if (method !== 'new') {
      setSelectedCard(method);
      setStep('paytoken');
    } else {
      setSelectedCard('');
      setStep('newpayment');
    }
  };

  const chooseVrm = (plate: string) => {
    if (plate !== 'new') {
      setSelectedVrm(plate);
      setVrm(plate);
    } else {
      setSelectedVrm('new');
      setVrm(typedVrm);
    }
  };

  // Single entry point for every plate change coming from PlateSelector —
  // stepping through the arrows and committing an inline edit both land here.
  const handlePlateChange = (value: string) => {
    const next = value.trim();
    if (!next) return;
    const resolved = resolvePlateSelection(next, plates);
    setSelectedVrm(resolved.selectedVrm);
    setVrm(resolved.vrm);
  };

  // Remember the freely typed plate from whatever set it (entry field, inline
  // edit, or the token step) so the arrows can always step back to it. Never
  // capture a plate that is already stored: the typed slot would dedupe away
  // and the arrows would be left cycling stored plates only.
  useEffect(() => {
    const entered = vrm.trim();
    if (
      selectedVrm === 'new' &&
      entered &&
      !plates.some((plate) => plateEquals(plate.vrm, entered))
    ) {
      setTypedVrm(entered);
    }
  }, [vrm, selectedVrm, plates]);

  // The token step collects the email inline (below the card) and hides the
  // notification checkbox, so keep the flag in step with the field the user is
  // actually being asked to fill in.
  useEffect(() => {
    if (step === 'paytoken') {
      setReceiveEmails(true);
    }
  }, [step, setReceiveEmails]);

  const loadingButton =
    validateMfaLoading ||
    paymentLinkLoading ||
    hasTokenLoading ||
    payWithTokenLoading ||
    fetchingTopupTimeline;

  useEffect(() => {
    setReachedMaxDuration(false);
  }, [selectedFee]);

  // Backend already filters specialTariffPricing by tariffType + subdomain.
  const specialTariffsToRender = selectedSite?.specialTariffPricing ?? [];

  useEffect(() => {
    if (selectedSite) {
      if (
        session &&
        session.validateRenewal &&
        session.validateRenewal.successful
      ) {
        const { renewalInfo } = session.validateRenewal;
        // @ts-ignore
        const { feeType } = renewalInfo;
        setSelectedFee(
          feeType === 'MONTHLY'
            ? FeeType.Monthly
            : feeType === 'WEEKLY'
              ? FeeType.Weekly
              : FeeType.Hourly,
        );
      } else {
        // Always start with Hourly, special tariffs will auto-apply if applicable
        setSelectedFee(FeeType.Hourly);
      }
    }
  }, [selectedSite]);

  useEffect(() => {
    if (!selectedSite) return;

    const isSpecial = isSpecialDomain(selectedSite);
    const activeTariffs = selectedSite.specialTariffPricing ?? [];
    const domain = process.env.REACT_APP_DOMAIN || '';

    // Only redirect if we're not already on the correct URL to prevent infinite loop
    if (isSpecial && activeTariffs.length === 0 && selectedSite.qrCode) {
      const redirectUrl = `${domain}/site/${selectedSite.qrCode}`;
      const currentUrl = window.location.href;

      // Check if we're already on the target URL to prevent infinite redirect
      if (!currentUrl.includes(`/site/${selectedSite.qrCode}`)) {
        window.location.href = redirectUrl;
      }
    }
  }, [selectedSite]);

  const locationCondition = !!location;

  // Update currentTime ref every second, but only update state when minute changes
  // This prevents unnecessary re-renders while keeping time calculations accurate
  useEffect(() => {
    const interval = setInterval(() => {
      const now = DateTime.now().setZone(ZONE);
      currentTimeRef.current = now;
      // Only update state if the minute changed (for display purposes)
      // This reduces re-renders from every 10 seconds to every minute
      const currentMinute = currentTime.minute;
      const newMinute = now.minute;
      if (currentMinute !== newMinute) {
        setCurrentTime(now);
      }
    }, 1000); // Check every second, but only update state when minute changes
    return () => clearInterval(interval);
  }, [currentTime.minute]);

  const emailCondition = (receiveEmails && email) || !receiveEmails;
  const smsCondition = (receiveSms && phone) || !receiveSms;
  const creditCardCondition =
    (checkedCreditCard && phone) || !checkedCreditCard;
  const vrmCondition =
    (['initial', 'token'].includes(step) && !vrm) ||
    (step !== 'initial' && vrm && vrm.trim().length > 0) ||
    postPaymentSite;
  const specialTariffCondition =
    !loadedBySpecialTariff ||
    specialTariffSelected !== null ||
    (useTimeline && !!currentTimelineStep?.specialTariffName);

  // Allow proceeding when step is 'newVrm' and VRM is valid
  const canContinueFromVrm = vrm && vrm.trim().length > 0;

  // payWithToken is rejected by the API without an email, so the token step
  // asks for one up front instead of letting the user hit that error.
  const requiresEmailForToken = step === 'paytoken';
  const tokenEmailInvalid = requiresEmailForToken && !isValidEmail(email);

  const canBuy =
    !fetchingTopupTimeline &&
    !tokenEmailInvalid &&
    (canContinueFromVrm ||
      (locationCondition &&
        durationCondition &&
        emailCondition &&
        smsCondition &&
        creditCardCondition &&
        vrmCondition &&
        specialTariffCondition));

  const isUsingStoredCard = useMemo(() => {
    return checkedCreditCard;
  }, [checkedCreditCard]);

  const specialTariff = specialTariffsToRender.find(
    (x) => x.id === specialTariffSelected,
  );

  return (
    <>
      <Dialog
        open={isSessionStale}
        onClose={() => setIsSessionStale(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {t('staleSessionTitle', 'Let’s refresh your parking details')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t(
              'staleSessionBody',
              'You’ve been on this page for a little while, so your parking details may no longer be up to date. To make sure you see the latest availability and pricing, please refresh before completing your payment.',
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="primary"
            onClick={handleStaleSessionRefresh}
            sx={{
              fontSize: '1rem',
            }}
          >
            {t('staleSessionRefreshButton', 'Refresh now')}
          </Button>
        </DialogActions>
      </Dialog>
      {!sitesLoading && (
        <SiteNotificationsAlerts
          notifications={globalSiteNotifications}
          dismissedIds={dismissedNotificationIds}
          onDismiss={dismissSiteNotification}
          closeLabel={t('dismissNotification', 'Dismiss notification')}
        />
      )}
      {!postPaymentSite && !postPaymentVrm && (
        <SiteSelectorComponent
          loadedBySpecialTariff={loadedBySpecialTariff}
          validateRenewalLoading={validateRenewalLoading}
        />
      )}
      {!sitesLoading && selectedSite && (
        <SiteNotificationsAlerts
          notifications={selectedSiteNotifications}
          dismissedIds={dismissedNotificationIds}
          onDismiss={dismissSiteNotification}
          closeLabel={t('dismissNotification', 'Dismiss notification')}
        />
      )}
      {location &&
        selectedSite &&
        selectedSite.siteStatus === SiteStatus.Closed &&
        !sitesLoading && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '40vh',
              px: 2,
              textAlign: 'center',
            }}
          >
            <Typography variant="h6" component="p">
              {selectedSite.closedMessage ||
                t('siteClosed', 'This site is currently closed')}
            </Typography>
          </Box>
        )}
      {location &&
        selectedSite &&
        selectedSite.siteStatus === SiteStatus.Open &&
        selectedSite.paymentType === PaymentType.POSTPAYMENT &&
        !postPaymentSite &&
        !postPaymentVrm &&
        !sitesLoading && (
          <PostPayment
            site={selectedSite}
            specialTariffMessage={specialTariff?.message}
          />
        )}
      {location &&
        selectedSite &&
        selectedSite.siteStatus === SiteStatus.Open &&
        (selectedSite.paymentType === PaymentType.PREPAYMENT ||
          (selectedSite.paymentType === PaymentType.POSTPAYMENT &&
            postPaymentSite &&
            postPaymentVrm)) &&
        !sitesLoading && (
          <>
            {/* Show VRM input when step is newVrm or VRM is empty */}
            {(step === 'newVrm' || !vrm || vrm.trim().length === 0) &&
              !postPaymentSite &&
              !postPaymentVrm && (
                <Stack spacing={2} alignItems="center" sx={{ width: '100%' }}>
                  <VrmComponent vrm={vrm} setVrm={setVrm} disabled={false} />
                </Stack>
              )}
            {/* Show main payment UI only when VRM is entered AND user has clicked Continue (step is not 'newVrm') */}
            {vrm && vrm.trim().length > 0 && step !== 'newVrm' && (
              <>
                {/* Single plate control: active plate, arrows across stored
                    plates, inline edit and per-plate delete */}
                <PlateSelector
                  vrm={vrm}
                  plates={plates}
                  typedVrm={typedVrm}
                  onPlateChange={handlePlateChange}
                  markPlateToDelete={markPlateToDelete}
                />
                {!loadedBySpecialTariff &&
                  ((selectedSite.hasMonthly &&
                    selectedSite.availableMonthlyPasses > 0) ||
                    (selectedSite.hasWeekly &&
                      selectedSite.availableWeeklyPasses > 0)) && (
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      width="100%"
                      alignItems="center"
                      justifyContent="center"
                      flexWrap="wrap"
                      gap={2}
                    >
                      <ButtonBase
                        sx={{
                          ...isChipSelected(
                            FeeType.Hourly,
                            selectedFee,
                            null,
                            null,
                            selectedTheme,
                          ),
                          minWidth: 120,
                          justifyContent: 'center',
                          borderRadius: '20px',
                        }}
                        onClick={() => {
                          setSelectedFee(FeeType.Hourly);
                        }}
                      >
                        {t('hourlyFee')}
                      </ButtonBase>

                      {selectedSite.hasMonthly &&
                        selectedSite.availableMonthlyPasses > 0 && (
                          <ButtonBase
                            sx={{
                              ...isChipSelected(
                                FeeType.Monthly,
                                selectedFee,
                                null,
                                null,
                                selectedTheme,
                              ),
                              minWidth: 120,
                              justifyContent: 'center',
                              borderRadius: '20px',
                            }}
                            onClick={() => {
                              setSelectedFee(FeeType.Monthly);
                            }}
                          >
                            {t('monthlyFee')}
                          </ButtonBase>
                        )}

                      {selectedSite.hasWeekly &&
                        selectedSite.availableWeeklyPasses > 0 && (
                          <ButtonBase
                            sx={{
                              ...isChipSelected(
                                FeeType.Weekly,
                                selectedFee,
                                null,
                                null,
                                selectedTheme,
                              ),
                              minWidth: 120,
                              justifyContent: 'center',
                              borderRadius: '20px',
                            }}
                            onClick={() => {
                              setSelectedFee(FeeType.Weekly);
                            }}
                          >
                            {t('weeklyFee')}
                          </ButtonBase>
                        )}
                    </Stack>
                  )}
                {((selectedSite.hasMonthly &&
                  selectedFee === FeeType.Monthly) ||
                  (selectedSite.hasWeekly &&
                    selectedFee === FeeType.Weekly)) && (
                  <Stack>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        p: 1,
                        borderRadius: 1,
                        border: `1px solid ${
                          selectedTheme?.primaryColor ?? colors.orange
                        }`,
                        cursor: 'pointer',
                        mt: 1,
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                      }}
                      onClick={() => history.push('/manage-pass')}
                    >
                      <Settings fontSize="small" sx={{ mr: 1 }} />
                      <Typography
                        variant="body2"
                        color={selectedTheme?.primaryColor ?? colors.orange}
                      >
                        {t('managePasses', 'Manage passes')}
                      </Typography>
                    </Box>
                  </Stack>
                )}
                {selectedFee === FeeType.Hourly && (
                  <>
                    <Stack
                      direction={'row'}
                      width={'100%'}
                      justifyContent="space-evenly"
                      alignItems={'center'}
                    >
                      {!shouldHideButtons && (
                        <Button
                          variant="outlined"
                          onClick={() => decreaseDuration(selectedSite)}
                          sx={{
                            borderRadius: '50%',
                            height: '5rem',
                            width: '5rem',
                          }}
                          disabled={
                            useTimeline
                              ? !selectedSite?.timeline || timelineIndex <= 0
                              : duration <= (selectedSite?.minimumTime ?? 0)
                          }
                        >
                          -
                        </Button>
                      )}
                      <Stack alignItems={'center'}>
                        <Typography
                          fontWeight={'bold'}
                          fontSize={'2rem'}
                          color={colors.blue}
                        >
                          ${amount.toFixed(2)}
                        </Typography>
                        {paidUntilTime && (
                          <Typography color={colors.blue} fontSize={'1rem'}>
                            {t('paidUntilText', {
                              timeToShow: paidUntilTime.toFormat('t'),
                            })}
                          </Typography>
                        )}
                        {useTimeline &&
                          currentTimelineStep?.specialTariffName && (
                            <Typography
                              color={colors.blue}
                              fontSize={'0.9rem'}
                              sx={{ mt: 0.5, fontStyle: 'italic' }}
                            >
                              {currentTimelineStep.specialTariffName}
                            </Typography>
                          )}
                      </Stack>
                      {!shouldHideButtons && (
                        <Button
                          variant="outlined"
                          onClick={() =>
                            increaseDuration(
                              selectedSite,
                              selectedFee,
                              specialTariffSelected,
                            )
                          }
                          disabled={
                            useTimeline
                              ? !selectedSite?.timeline ||
                                timelineIndex >=
                                  selectedSite.timeline.length - 1
                              : reachedMaxDuration
                          }
                          sx={{
                            borderRadius: '50%',
                            height: '5rem',
                            width: '5rem',
                          }}
                        >
                          +
                        </Button>
                      )}
                    </Stack>
                    <Typography color={colors.blue} textAlign={'center'}>
                      {' '}
                      {t('timeAdjustmentInfo', {
                        timeStep: selectedSite?.timeStep,
                        minimumTime: selectedSite?.minimumTime,
                      })}
                    </Typography>
                    {selectedSite.hasFreeTime && (
                      <Typography
                        color={colors.blue}
                        textAlign={'center'}
                      >{`Free time: ${selectedSite.freeTime} minutes included`}</Typography>
                    )}
                  </>
                )}
                {selectedFee === FeeType.Monthly && (
                  <Stack
                    alignItems={'center'}
                    height="5rem"
                    justifyContent={'center'}
                  >
                    <Typography
                      fontWeight={'bold'}
                      fontSize={'2rem'}
                      color={colors.blue}
                    >
                      4 {t('weeks')}
                    </Typography>
                  </Stack>
                )}
                {selectedFee === FeeType.Weekly && (
                  <Stack
                    alignItems={'center'}
                    height="5rem"
                    justifyContent={'center'}
                  >
                    <Typography
                      fontWeight={'bold'}
                      fontSize={'2rem'}
                      color={colors.blue}
                    >
                      {t('weeklyTime')}
                    </Typography>
                  </Stack>
                )}
                {selectedFee === FeeType.SpecialTariff && (
                    <Stack
                      alignItems={'center'}
                      height="5rem"
                      justifyContent={'center'}
                      gap={1}
                    >
                      <Typography
                        fontWeight={'bold'}
                        sx={{
                          fontSize: {
                            xs: '1.5rem',
                            sm: '2rem',
                          },
                        }}
                        color={colors.blue}
                        textAlign={'center'}
                      >
                        {getSpecialTariffText(
                          selectedSite,
                          specialTariffsToRender,
                          specialTariffSelected,
                          specialTariff?.message,
                        )}
                      </Typography>
                    </Stack>
                  )}
                <Stack alignItems={'center'}>
                  <Typography textAlign="center" color={colors.blue}>
                    {selectedSite.transactionFee > 0
                      ? t('transactionFeeWithAmount', {
                          amount: selectedSite.transactionFee.toFixed(2),
                        })
                      : t('transactionFeeWithoutAmount')}
                  </Typography>
                  <FormGroup>
                    <Stack
                      direction={'row'}
                      alignItems="center"
                      justifyContent={'space-between'}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            onChange={handleCreditCardChange}
                            checked={checkedCreditCard}
                          />
                        }
                        label={t('storeOrUseCards')}
                      />
                      <Tooltip text={t('cardStorageTooltip')} />{' '}
                    </Stack>
                    <ReceiveSMSComponent
                      checkedCreditCard={checkedCreditCard}
                    />
                    {receiveSms && (
                      <PhoneComponent phone={phone} setPhone={setPhone} />
                    )}
                    {/* In the token step the email is asked for directly below
                        the card instead, so this opt-in is hidden there. */}
                    {!requiresEmailForToken && (
                      <>
                        <ReceiveEmailComponent />
                        {receiveEmails && (
                          <EmailComponent email={email} setEmail={setEmail} />
                        )}
                      </>
                    )}
                  </FormGroup>
                </Stack>
                {requestToken && step === 'token' && countdown && (
                  <>
                    <TimerComponent countdown={countdown} />
                    <MFAComponent mfa={mfa} setMfa={setMfa} />
                  </>
                )}
                {tokens.length > 0 && (
                  <>
                    <Typography color={colors.blue}>
                      {t('selectPaymentMethod')}
                    </Typography>
                    <Carousel
                      sx={{ width: '100%' }}
                      autoPlay={false}
                      swipe={true}
                      fullHeightHover={true}
                      navButtonsAlwaysVisible={true}
                      strictIndexing={false}
                      indicators={false}
                      onChange={(now) => {
                        if (now === undefined) return;
                        if (now >= tokens.length) {
                          choosePaymentMethod('new');
                        } else {
                          choosePaymentMethod(tokens[now].id);
                        }
                      }}
                    >
                      {getCards(tokens, markCardToDelete, selectedCard)}
                    </Carousel>
                  </>
                )}
                {requiresEmailForToken && (
                  <TextField
                    label={t('emailLabel')}
                    value={email}
                    type="email"
                    autoComplete="email"
                    required
                    fullWidth
                    onChange={(e) => setEmail(e.target.value)}
                    onBlur={() => setEmailTouched(true)}
                    error={emailTouched && tokenEmailInvalid}
                    helperText={
                      emailTouched && tokenEmailInvalid
                        ? email.trim()
                          ? t('emailInvalid')
                          : t('emailRequired')
                        : t('emailReceiptHelpText')
                    }
                  />
                )}
              </>
            )}
            {error && <Typography color={'red'}>{error}</Typography>}
            {showPaymentFlow && (
              <PaymentFlow
                site={selectedSite}
                details={buildPaymentSessionDetails()}
                setStep={setStep}
                processPaymentCallback={processPayment}
                setVrm={setVrm}
                open={showPaymentFlow}
                onClose={() => setShowPaymentFlow(false)}
                amount={amount}
                paymentLinkLoading={paymentLinkLoading}
                showVrm={selectedSite?.paymentType === PaymentType.POSTPAYMENT}
              />
            )}
            <Button
              fullWidth
              disabled={!canBuy}
              ref={bottomRef}
              sx={{ padding: 2 }}
              variant="contained"
              onClick={async () => {
                if (step === 'newVrm' && vrm && vrm.trim().length > 0) {
                  // Fetch topup timeline before continuing
                  await fetchAndApplyTopupTimeline();
                  // Continue from VRM entry to normal flow
                  setStep('initial');
                } else if (isUsingStoredCard) {
                  processPayment(selectedSite, buildPaymentSessionDetails());
                } else {
                  setShowPaymentFlow(true);
                }
              }}
            >
              {loadingButton ? (
                <ClipLoader color="white" />
              ) : step === 'newVrm' ? (
                t('continue', 'Continue')
              ) : step === 'token' ? (
                t('validate')
              ) : selectedFee === FeeType.Monthly ||
                selectedFee === FeeType.Weekly ? (
                t('payAmount', { amount: amount.toFixed(2) })
              ) : (
                t('pay')
              )}
            </Button>
          </>
        )}
      <DeletePopover
        selectedSite={selectedSite}
        tokens={tokens}
        plates={plates}
        chooseVrm={chooseVrm}
      />
    </>
  );
};
